import React from "react";
import { Input, Tag, Space, Button, Typography, Tooltip } from "antd";

import { SwapOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
const { Text } = Typography;

const getDecimalPrecision = () => {
  try { const s = localStorage.getItem("global_decimal_precision"); return s ? parseInt(s, 10) : 3; } catch { return 3; }
};

// Get global display currency settings
const getGlobalDisplayCurrency = () => {
  try {
    const currency = localStorage.getItem("global_display_currency") || "USD";
    const SYMBOLS = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      CAD: "C$",
      AUD: "A$",
      CHF: "CHF",
      INR: "₹",
    };
    const symbol = SYMBOLS[currency] || currency;
    if (currency === "USD") return { currency, symbol, rate: 1 };
    const ratesStr = localStorage.getItem("custom_currency_rates");
    const rates = ratesStr ? JSON.parse(ratesStr) : {};
    return { currency, symbol, rate: rates[currency] || null };
  } catch (e) {
    return { currency: "USD", symbol: "$", rate: 1 };
  }
};

// Formatter that uses global display currency
// All amounts from backend are in USD, convert to display currency if set
const formatUSD = (amount) => {
  if (amount == null || amount === undefined) return `$${(0).toFixed(getDecimalPrecision())}`;
  const value = parseFloat(amount);
  const dp = getDecimalPrecision();

  const {
    currency: displayCurrency,
    symbol: displaySymbol,
    rate,
  } = getGlobalDisplayCurrency();

  if (displayCurrency !== "USD" && rate && rate > 0) {
    const converted = value * rate;
    return `${displaySymbol} ${converted.toLocaleString("en-US", {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    })}`;
  }

  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: dp,
    maximumFractionDigits: dp,
  })}`;
};

// Helper to parse numbers that may contain commas, dollar signs, or other formatting
// parseFloat("18,132") returns 18, but we want 18132
// parseFloat("$20,670.60") returns NaN, but we want 20670.60
const parseNumberWithCommas = (value) => {
  if (value == null || value === undefined || value === "") return 0;
  // Convert to string and remove currency symbols ($, €, £, etc.), commas, and spaces
  const cleanedValue = String(value)
    .replace(/[$€£¥₹,\s]/g, "") // Remove currency symbols, commas, and spaces
    .trim();
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? 0 : parsed;
};
/**
 * Generate invoice columns for the payment allocation table
 * @param {Object} params - Parameters needed for column functionality
 * @param {Array} params.invoices - List of invoices
 * @param {Array} params.allocations - List of payment allocations
 * @param {Function} params.handlePaymentChange - Function to handle payment amount changes
 * @param {Function} params.handleAllocatePayment - Function to handle payment allocation
 * @param {Boolean} params.loadingAllocation - Loading state for allocation actions
 * @param {Object} params.filterOptions - Object containing filter options for each column
 * @param {Object} params.currentFilters - Object containing current filter values
 * @returns {Array} Array of column definitions for invoice table
 */
const getInvoiceColumns = ({
  invoices,
  allocations,
  handlePaymentChange,
  handleAllocatePayment,
  loadingAllocation,
  filterOptions = {},
  currentFilters = {},
  transactionRemainingAmount = 0, // Remaining amount available on the transaction
  onViewPartner,
}) => [
  {
    title: "Invoice #",
    dataIndex: "number",
    key: "number",
    filterMultiple: true,
    filterSearch: true,
    filteredValue: currentFilters.number || null,
    filters: filterOptions.invoiceNumbers || [],
    onFilter: (value, record) => record.number === value,
    width: 110,
  },
  {
    title: "Allocated",
    key: "allocated_from_txn",
    render: (_, record) => {
      const amt =
        record.currentAllocationAmount !== undefined
          ? record.currentAllocationAmount
          : parseNumberWithCommas(record.paymentAmount || 0);
      return formatUSD(amt || 0);
    },
    width: 110,
  },
  {
    title: "Partner",
    dataIndex: "partner_name",
    key: "partner_name",
    render: (partnerName, record) => {
      // Get partner name from various possible fields
      const name = partnerName || record?.partner?.name || "N/A";
      // Get partner ID from various possible fields
      const partnerId = record?.partner_id || record?.partner?.id || (typeof record?.partner === "number" ? record?.partner : null);
      if (partnerId && onViewPartner) {
        return (
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              onViewPartner(partnerId);
            }}
            style={{ color: "#1890ff", cursor: "pointer" }}
            title="View partner transactions"
          >
            {name}
          </a>
        );
      }
      return name;
    },
    width: 130,
    ellipsis: true,
    filterMultiple: true,
    filterSearch: true,
    filteredValue: currentFilters.partner_name || null,
    filters: filterOptions.partners || [],
    onFilter: (value, record) => (record.partner_name || record?.partner?.name || "") === value,
  },
  {
    title: "Brand",
    dataIndex: "brand_name",
    key: "brand_name",
    render: (brandName, record) => {
      const name = brandName || record?.brand?.name || "";
      return name || "N/A";
    },
    width: 110,
    ellipsis: true,
    filterMultiple: true,
    filterSearch: true,
    filteredValue: currentFilters.brand_name || null,
    filters: filterOptions.brands || [],
    onFilter: (value, record) => (record.brand_name || record?.brand?.name || "") === value,
  },
  {
    title: "Issue Date",
    dataIndex: "issue_date",
    key: "issue_date",
    render: (date) => (date ? dayjs(date).format("MM/DD/YY") : ""),
    sorter: true,
    width: 100,
  },
  {
    title: "Due Date",
    dataIndex: "due_date",
    key: "due_date",
    render: (date) => (date ? dayjs(date).format("MM/DD/YY") : ""),
    sorter: true,
    width: 100,
  },
  {
    title: "Terms",
    dataIndex: "payment_terms_code",
    key: "payment_terms_code",
    render: (terms) => (terms ? terms : "N/A"),
    filterMultiple: true,
    filterSearch: true,
    filteredValue: currentFilters.payment_terms_code || null,
    filters: filterOptions.paymentTerms || [],
    onFilter: (value, record) => record.payment_terms_code === value,
    width: 90,
    ellipsis: true,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    render: (status, record) => {
      let color = "default";
      if (status === "paid") color = "success";
      if (status === "partial") color = "warning";
      if (status === "overdue") color = "error";
      if (status === "draft") color = "blue";

      // Check if invoice is pending approval
      const approvalStatus = record.approval_status || "draft";
      const isApproved = approvalStatus === "approved";

      return (
        <Space direction="vertical" size={2}>
          <Tag color={color} style={{ fontSize: 11 }}>
            {record.status_display || (status ? status.toUpperCase() : "")}
          </Tag>
          {!isApproved && (
            <Tag color="orange" style={{ fontSize: 10 }}>
              PENDING APPROVAL
            </Tag>
          )}
        </Space>
      );
    },
    filterMultiple: true,
    filterSearch: true,
    filteredValue: currentFilters.status || null,
    filters: filterOptions.status || [
      { text: "Paid", value: "paid" },
      { text: "Draft", value: "draft" },
    ],
    onFilter: (value, record) => record.status === value,
    width: 100,
  },
  {
    title: "Total",
    dataIndex: "total_amount",
    key: "total_amount",
    sorter: true,
    render: (amount) => formatUSD(amount || 0),
    width: 110,
  },
  {
    title: "Paid",
    dataIndex: "paid_amount",
    key: "paid_amount",
    render: (amount) => formatUSD(amount || 0),
    width: 100,
  },
  {
    title: "Remaining",
    dataIndex: "remainingAmount",
    key: "remaining",
    render: (amount) => formatUSD(amount || 0),
    width: 110,
  },
  {
    title: "Eligible",
    key: "eligible",
    render: (record) => {
      const invoiceRemaining = parseFloat(record.remainingAmount || 0);
      const isEligible = invoiceRemaining > 0 || transactionRemainingAmount > 0;
      return isEligible ? (
        <Tag color="green">Yes</Tag>
      ) : (
        <Tag color="red">No</Tag>
      );
    },
    width: 80,
  },
  {
    title: "Payment Amt",
    key: "payment",
    width: 130,
    render: (record) => {
      // Check approval status for visual hint
      const paymentApprovalStatus = record.approval_status || "draft";
      const isPaymentApproved = paymentApprovalStatus === "approved";

      // Show current allocation amount if it exists
      if (record.hasExistingAllocation) {
        return (
          <Input
            type="text"
            size="small"
            style={{ width: 100 }}
            value={record.paymentAmount}
            onChange={(e) => handlePaymentChange(record.id, e)}
            disabled={loadingAllocation}
            placeholder="Enter amount"
            status="success"
          />
        );
      }

      // For invoices without allocation, show input if:
      // 1. Invoice has remaining balance, OR
      // 2. Transaction has remaining amount (can allocate more even if invoice is fully paid)
      const invoiceRemaining = parseFloat(record.remainingAmount || 0);
      const canAllocate =
        invoiceRemaining > 0 || transactionRemainingAmount > 0;

      return canAllocate ? (
        <Input
          type="text"
          size="small"
          style={{ width: 100 }}
          value={record.paymentAmount}
          onChange={(e) => handlePaymentChange(record.id, e)}
          disabled={loadingAllocation}
          placeholder="Enter amount"
        />
      ) : (
        <Text type="secondary">N/A</Text>
      );
    },
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    width: 200,
    render: (_, record) => {
      // Determine if there's an existing allocation from any available data
      const hasAllocation =
        record.hasExistingAllocation ||
        !!record.allocationId ||
        !!allocations.find(
          (a) => a.invoice === record.id || a.invoice_id === record.id
        );

      // Always get the allocation ID if it exists
      // Resolve allocation id robustly from multiple shapes
      const allocationId =
        record.allocationId ||
        (allocations.find(
          (a) =>
            a.invoice === record.id ||
            a.invoice_id === record.id ||
            a.invoice_number === record.number
        )?.id ??
          null);

      // Check if invoice is approved - only approved invoices can be allocated
      const invoiceApprovalStatus = record.approval_status || "draft";
      const isInvoiceApproved = invoiceApprovalStatus === "approved";

      // Show Allocate/Unallocate buttons for all invoices (approved or draft)
      // Draft invoice allocations are stored as "pending" and auto-activated on approval
      return (
        <Space>
          <Tooltip title={!isInvoiceApproved ? "Allocation will be pending until management approves this invoice" : ""}>
            <Button
              type="primary"
              size="small"
              style={!isInvoiceApproved ? { borderColor: '#faad14', background: '#faad14' } : {}}
              onClick={() => {
                const amount = record.paymentAmount
                  ? parseNumberWithCommas(record.paymentAmount)
                  : 0;
                handleAllocatePayment(
                  record.id,
                  amount,
                  hasAllocation ? allocationId : null
                );
              }}
              disabled={
                loadingAllocation ||
                (!(parseFloat(record.remainingAmount || 0) > 0) &&
                  transactionRemainingAmount <= 0) ||
                !(parseNumberWithCommas(record.paymentAmount || 0) > 0)
              }
            >
              {isInvoiceApproved ? "Allocate" : "Allocate (Pending)"}
            </Button>
          </Tooltip>

          {/* Unallocate button - enabled when allocation exists */}
          <Button
            danger
            size="small"
            onClick={() => {
              handleAllocatePayment(record.id, 0, allocationId);
            }}
            disabled={!hasAllocation || loadingAllocation}
            title={
              !hasAllocation ? "No allocation to remove" : "Remove allocation"
            }
          >
            Unallocate
          </Button>
        </Space>
      );
    },
  },
];

export default getInvoiceColumns;
